import React, { useState } from "react";
import {
    Box,
    TextareaAutosize,
    Button,
    Paper,
    Typography,
    Card,
    CardHeader,
    CardContent,
    Divider,
    CircularProgress,
    Tooltip,
    IconButton,
    useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ApiDetails } from "../../../../dummyData";
import { showSnackbar } from "../../../../components/SnackBar/SnackBar";

// Icons
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    backgroundColor: "#2C5283",
    color: "white",
    padding: "16px 20px",
    "& .MuiCardHeader-title": {
        fontWeight: 600,
        fontSize: "1.1rem",
    },
    "& .MuiCardHeader-subheader": {
        color: "rgba(255, 255, 255, 0.7)",
    }
}));

const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
    width: "100%",
    padding: "16px",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    outline: "none",
    resize: "none",
    fontSize: "16px",
    fontFamily: "Inter, Arial, sans-serif",
    lineHeight: "1.5",
    transition: "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
        borderColor: "#2C5283",
    },
    "&:focus": {
        borderColor: "#2C5283",
        boxShadow: "0 0 0 2px rgba(44, 82, 131, 0.2)",
    },
    "&::placeholder": {
        color: "#9E9E9E",
    }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#2C5283",
    color: "white",
    padding: "8px 24px",
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "0 2px 10px rgba(44, 82, 131, 0.25)",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        backgroundColor: "#1E3A63",
        boxShadow: "0 4px 15px rgba(44, 82, 131, 0.35)",
    }
}));

const ClearButton = styled(Button)(({ theme }) => ({
    color: "#d32f2f",
    padding: "8px 24px",
    borderRadius: "8px",
    borderColor: "#d32f2f",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
        backgroundColor: "rgba(211, 47, 47, 0.04)",
        borderColor: "#b71c1c",
    }
}));

const CharCounter = styled(Typography)(({ theme, count, limit }) => ({
    fontSize: "0.75rem",
    color: count > limit * 0.9 ? (count >= limit ? "#d32f2f" : "#ff9800") : "#757575",
    margin: "4px 0 12px 0",
    textAlign: "right",
}));

export default function MessagesComponent(props) {
    const { propertyDetails, originalText } = props;
    const theme = useTheme();

    const [broadcastMessage, setBroadcastMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Character limit
    const characterLimit = 500;

    // Handle sending a message
    const sendBroadcastMessage = () => {
        if (broadcastMessage.trim() === "") {
            showSnackbar({
                title: "Error!",
                message: "Cannot send an empty message",
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        if (broadcastMessage.length > characterLimit) {
            showSnackbar({
                title: "Error!",
                message: `Message exceeds the ${characterLimit} character limit`,
                type: "error",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        setIsLoading(true);

        let values = {
            message: broadcastMessage,
            property_id: propertyDetails.property_id,
            landlord_id: JSON.parse(originalText)[0][1],
        };

        fetch(ApiDetails + "pegasus/visionary/messages/broadcast", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"  // Replace with your actual token variable
            },
            body: JSON.stringify(values),
        })
            .then(async (response) => {
                setIsLoading(false);
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Message sent successfully to all tenants.",
                        type: "success",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                    // Clear the textarea after successful send
                    setBroadcastMessage("");
                } else if (response.status === 500) {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Kindly try again later.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                }
            })
            .catch((err) => {
                setIsLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Kindly try again later.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            });
    };

    // Handle clearing the message
    const handleClearMessage = () => {
        if (broadcastMessage.trim() !== "") {
            setBroadcastMessage("");
        }
    };

    return (
        <Box sx={{ height: "100%", minHeight: "60vh" }}>
            <StyledCard>
                <StyledCardHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <NotificationsActiveIcon sx={{ mr: 1 }} />
                            Broadcast Message to Tenants
                        </Box>
                    }
                    subheader={`Property: ${propertyDetails.property_name || "All Units"}`}
                    action={
                        <Tooltip title="Send announcements, important updates, or general information to all tenants in this property">
                            <IconButton color="inherit">
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    }
                />

                <CardContent sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    p: 3
                }}>
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Compose your message below:
                        </Typography>
                    </Box>

                    {/* Message text area */}
                    <Box sx={{ flexGrow: 1 }}>
                        <StyledTextarea
                            minRows={12}
                            maxRows={20}
                            placeholder="Type your announcement or notification here... This message will be sent to all tenants of this property."
                            value={broadcastMessage}
                            onChange={(e) => setBroadcastMessage(e.target.value)}
                        />

                        <CharCounter
                            count={broadcastMessage.length}
                            limit={characterLimit}
                        >
                            {broadcastMessage.length}/{characterLimit} characters
                        </CharCounter>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Action buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2
                        }}
                    >
                        <Box>
                            <Tooltip title="Clear message">
                                <ClearButton
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleClearMessage}
                                    disabled={broadcastMessage.trim() === "" || isLoading}
                                >
                                    Clear
                                </ClearButton>
                            </Tooltip>
                        </Box>

                        <Box>
                            <SubmitButton
                                variant="contained"
                                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                onClick={sendBroadcastMessage}
                                disabled={broadcastMessage.trim() === "" || isLoading || broadcastMessage.length > characterLimit}
                            >
                                {isLoading ? "Sending..." : "Send to All Tenants"}
                            </SubmitButton>
                        </Box>
                    </Box>
                </CardContent>
            </StyledCard>
        </Box>
    );
}